import { RequestStatus } from '@features/request/requestSlice';
import type { TUseRequestUpdaterProps } from '@features/request/useRequest';
import { useRequest } from '@features/request/useRequest';
import { useResetStoreData } from '@features/request/useResetStoreData';
import {
    type CatalogRatioDataForList,
    type CatalogRatioGroupData,
    type CatalogRatiosQuery,
    RatioTypeEnum,
} from '@gql/graphql';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useCatalogRatiosSubgroupsData } from '../../AddRatiosModal/hooks';
import { catalogRatiosGql } from '../../CatalogRatios';
import { getWhereColumnFilters } from '../helpers';

type TUseMassApplicationRatioData = {
    filters: Record<string, string[]>;
    ratioType?: RatioTypeEnum;
    selectedRatioGroup?: CatalogRatioGroupData | null;
    isConstructionWorksGroupSelected: boolean;
};

const requestId = 'massApplicationRatioData';

export const useMassApplicationRatioData = ({
    filters,
    ratioType,
    selectedRatioGroup,
    isConstructionWorksGroupSelected,
}: TUseMassApplicationRatioData) => {
    const [data, setData] = useState<CatalogRatioDataForList[]>([]);
    const [responseRatioType, setResponseRatioType] = useState<RatioTypeEnum>();
    const [responseGroupId, setResponseGroupId] = useState<string | number>();

    const dispatch = useDispatch();

    const [coefficientsReset] = useResetStoreData({ requestId });

    const ratioGroupId = useMemo(() => selectedRatioGroup?.id, [selectedRatioGroup?.id]);

    const { subgroupsData, subgroupsStatus } = useCatalogRatiosSubgroupsData({
        ratioGroupId,
        effect: true,
    });

    const subGroupsNames = useMemo(() => subgroupsData.map((subgroup) => subgroup.name), [subgroupsData]);

    const reset = useCallback(() => {
        coefficientsReset(undefined);
    }, [coefficientsReset]);

    const variables = useMemo(
        () => ({
            where: {
                ratioType: { eq: ratioType },
                ...(ratioType !== RatioTypeEnum.TechPartCoef && !isConstructionWorksGroupSelected
                    ? {
                          catalogRatioGroupId: { eq: ratioGroupId },
                      }
                    : {}),
                ...(ratioType === RatioTypeEnum.DismantlingCoef &&
                isConstructionWorksGroupSelected &&
                subGroupsNames.length
                    ? {
                          and: [
                              {
                                  catalogRatioGroupName: {
                                      in: subGroupsNames,
                                  },
                              },
                          ],
                      }
                    : {}),
                ...(ratioType && getWhereColumnFilters(filters)),
                ...(ratioType !== RatioTypeEnum.TechPartCoef ? { hasCosts: { eq: false } } : {}),
            },
        }),
        [ratioType, isConstructionWorksGroupSelected, ratioGroupId, subGroupsNames, filters],
    );

    const onSuccess = useCallback(
        (depsVars: TUseRequestUpdaterProps<CatalogRatiosQuery>) => {
            setData(depsVars?.payload?.catalogRatios?.items || []);
            setResponseRatioType(ratioType);
            if (ratioGroupId) {
                setResponseGroupId(ratioGroupId);
            }
            reset();
        },
        [ratioGroupId, ratioType, reset],
    );

    const effect = useMemo(() => {
        if (
            !ratioType ||
            ((subgroupsStatus === undefined || subgroupsStatus === RequestStatus.FETCHING) &&
                ratioType !== RatioTypeEnum.TechPartCoef)
        ) {
            return false;
        }
        if (ratioType !== RatioTypeEnum.TechPartCoef) {
            if (ratioGroupId) {
                if (ratioType === RatioTypeEnum.DismantlingCoef) {
                    return !(
                        isConstructionWorksGroupSelected &&
                        (subgroupsStatus !== RequestStatus.SUCCESS || !subgroupsData.length)
                    );
                }

                return true;
            }

            return false;
        }

        return true;
    }, [ratioType, subgroupsStatus, ratioGroupId, isConstructionWorksGroupSelected, subgroupsData.length]);

    const [, , , ratiosMakeRequest] = useRequest({
        requestId,
        query: catalogRatiosGql,
        variables,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const request = useCallback(() => {
        if (effect) {
            dispatch(ratiosMakeRequest(variables));
        }
    }, [variables, dispatch, ratiosMakeRequest, effect]);

    useEffect(() => {
        setData([]);
        reset();
        request();
    }, [request, reset]);

    return {
        reset,
        data,
        responseRatioType,
        responseGroupId,
        subgroupsData,
        subGroupsNames,
    };
};
